import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import TopBar from '../../components/TopBar';
import './Filosofia.css';

const Filosofia = () => {
  const [activeSection, setActiveSection] = useState('quantitative');
  const typedSections = useRef(new Set());

  // Función para revelar texto letra por letra (streaming de tokens sin alterar layout)
  const streamText = (element, speed = 6) => {
    return new Promise(resolve => {
      element.style.opacity = '1';
      const spans = Array.from(element.querySelectorAll('span:not(.blinking-cursor)'));
      let index = 0;

      const cursor = document.createElement('span');
      cursor.className = 'blinking-cursor';
      cursor.textContent = '_';

      if (spans.length > 0) {
        element.insertBefore(cursor, spans[0]);
      } else {
        element.appendChild(cursor);
      }

      function revealNextChar() {
        if (index < spans.length) {
          spans[index].style.opacity = '1';
          if (spans[index].nextSibling) {
            element.insertBefore(cursor, spans[index].nextSibling);
          } else {
            element.appendChild(cursor);
          }
          index++;
          let delay = speed + Math.random() * 2;
          if (index < spans.length && spans[index].textContent === ' ') {
            delay = 0;
          }
          setTimeout(revealNextChar, delay);
        } else {
          if (element.contains(cursor)) {
            element.removeChild(cursor);
          }
          resolve();
        }
      }

      if (spans.length > 0) {
        revealNextChar();
      } else {
        resolve();
      }
    });
  };

  // Función para disparar la secuencia de tipeo
  const triggerTypingSequence = useCallback(async (sectionId) => {
    if (typedSections.current.has(sectionId)) return;
    typedSections.current.add(sectionId);

    const section = document.getElementById(sectionId);
    if (!section) return;

    const titleLetterEl = section.querySelector('.title-letter');
    const subtitleEl = section.querySelector('.story-subtitle');
    const quoteEl = section.querySelector('.story-quote');
    const descEls = Array.from(section.querySelectorAll('.story-desc'));

    if (titleLetterEl) {
      titleLetterEl.style.opacity = '1';
    }

    await new Promise(r => setTimeout(r, 50));

    if (subtitleEl) {
      subtitleEl.style.opacity = '1';
    }

    if (quoteEl) {
      await streamText(quoteEl, 1);
    }

    for (const descEl of descEls) {
      await streamText(descEl, 0.8);
    }

    const cursors = section.querySelectorAll('.blinking-cursor');
    cursors.forEach(c => (c.style.opacity = '0'));

    setTimeout(() => {
      section.classList.add('typing-finished');
    }, 200);
  }, []);

  // Preparar spans para animación de texto una vez cargado el DOM
  useEffect(() => {
    const wrapSpans = (el) => {
      const text = el.textContent.trim();
      el.innerHTML = '';
      text.split('').forEach(char => {
        const span = document.createElement('span');
        span.textContent = char;
        span.style.opacity = '0';
        el.appendChild(span);
      });
    };

    ['quantitative', 'research', 'trading'].forEach(id => {
      const section = document.getElementById(id);
      if (!section) return;
      const quoteEl = section.querySelector('.story-quote');
      const descEls = section.querySelectorAll('.story-desc');
      if (quoteEl) wrapSpans(quoteEl);
      descEls.forEach(wrapSpans);
    });
  }, []);

  // IntersectionObserver para Scrollytelling
  useEffect(() => {
    const sectionIds = ['quantitative', 'research', 'trading'];
    const observerOptions = {
      root: null,
      rootMargin: '-10% 0px -10% 0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const targetId = entry.target.id;
          setActiveSection(targetId);
          entry.target.classList.add('active');
          triggerTypingSequence(targetId);
        }
      });
    }, observerOptions);

    sectionIds.forEach(id => {
      const elem = document.getElementById(id);
      if (elem) observer.observe(elem);
    });

    return () => observer.disconnect();
  }, [triggerTypingSequence]);

  // Manejo de navegación suave
  const scrollTo = (id) => {
    const target = document.getElementById(id);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="filosofia-container">
      {/* Cuadrícula estructural técnica de fondo */}
      <div className="bg-grid"></div>

      {/* Header Sticky Unificado */}
      <TopBar activeNav="filosofia" />

      {/* Indicador Lateral de Progreso (Minimalista) */}
      <div className="progress-nav">
        <div
          className={`progress-dot-wrapper ${activeSection === 'quantitative' ? 'active' : ''}`}
          onClick={() => scrollTo('quantitative')}
          title="quantitative"
        >
          <div className="progress-dash"></div>
        </div>
        <div
          className={`progress-dot-wrapper ${activeSection === 'research' ? 'active' : ''}`}
          onClick={() => scrollTo('research')}
          title="research"
        >
          <div className="progress-dash"></div>
        </div>
        <div
          className={`progress-dot-wrapper ${activeSection === 'trading' ? 'active' : ''}`}
          onClick={() => scrollTo('trading')}
          title="trading"
        >
          <div className="progress-dash"></div>
        </div>
      </div>

      {/* Contenedor del Scrollytelling de Filosofía */}
      <div className="story-container" style={{ paddingTop: '80px' }}>
        
        {/* 1. q - quantitative Section */}
        <section className="story-section" id="quantitative">
          <div className="story-grid">
            <div className="story-content">
              <h2 className="story-title">
                <span className="title-letter">q</span>
                <span className="blinking-cursor">_</span>
                <span className="story-subtitle">— el enfoque numérico y lógico</span>
              </h2>
              <div className="story-quote">
                La objetividad matemática como fundamento de la verdad operativa.
              </div>
              <p className="story-desc">
                La lógica y la objetividad numérica guían cada una de nuestras decisiones. Diseñamos modelos cuantitativos rigurosos basados en grandes volúmenes de datos abstractos. Así eliminamos por completo los sesgos cognitivos e intuiciones personales del proceso.
              </p>
              <p className="story-desc">
                Nuestras arquitecturas matemáticas aíslan el ruido externo de manera eficiente. Esto nos permite estructurar variables complejas con total precisión técnica. Convertimos la complejidad sistémica en escenarios claros, predecibles y altamente auditables.
              </p>
            </div>

            <div className="story-visual">
              <div className="bg-letter">q</div>
              {/* SVG: Curva Exponencial */}
              <svg className="visual-svg" viewBox="0 0 400 400">
                <line x1="20" y1="380" x2="380" y2="380" stroke="var(--text-main)" strokeWidth="0.8" />
                <line x1="20" y1="20" x2="20" y2="380" stroke="var(--text-main)" strokeWidth="0.8" />
                <path d="M 20,370 C 150,370 280,320 380,60" stroke="var(--text-main)" strokeWidth="1.5" fill="none" />
              </svg>
            </div>
          </div>
        </section>

        {/* 2. r - research Section */}
        <section className="story-section" id="research">
          <div className="story-grid">
            <div className="story-content">
              <h2 className="story-title">
                <span className="title-letter">r</span>
                <span className="blinking-cursor">_</span>
                <span className="story-subtitle">— la investigación y el método científico</span>
              </h2>
              <div className="story-quote">
                Validación empírica constante en entornos dinámicos.
              </div>
              <p className="story-desc">
                El método científico es nuestra herramienta para descubrir valor sostenible. Formulamos hipótesis rigurosas a partir de observaciones detalladas del entorno. Luego, sometemos cada supuesto a pruebas empíricas sumamente estrictas.
              </p>
              <p className="story-desc">
                Esta exploración continua nos asegura estructurar hipótesis estables en el tiempo. Desarrollamos investigación pura y aplicada para anticipar cambios sistémicos. De este modo, garantizamos que cada modelo cuente con validación científica comprobable.
              </p>
            </div>

            <div className="story-visual">
              <div className="bg-letter">r</div>
              {/* SVG: Espiral Áurea de Fibonacci */}
              <svg className="visual-svg" viewBox="0 0 233 144">
                <rect x="0" y="0" width="144" height="144" stroke="var(--text-main)" strokeWidth="0.5" fill="none" opacity="0.15" />
                <rect x="144" y="55" width="89" height="89" stroke="var(--text-main)" strokeWidth="0.5" fill="none" opacity="0.15" />
                <rect x="178" y="0" width="55" height="55" stroke="var(--text-main)" strokeWidth="0.5" fill="none" opacity="0.15" />
                <rect x="144" y="0" width="34" height="34" stroke="var(--text-main)" strokeWidth="0.5" fill="none" opacity="0.15" />
                <rect x="144" y="34" width="21" height="21" stroke="var(--text-main)" strokeWidth="0.5" fill="none" opacity="0.15" />
                <rect x="165" y="42" width="13" height="13" stroke="var(--text-main)" strokeWidth="0.5" fill="none" opacity="0.15" />
                <rect x="170" y="34" width="8" height="8" stroke="var(--text-main)" strokeWidth="0.5" fill="none" opacity="0.15" />
                <rect x="165" y="34" width="5" height="5" stroke="var(--text-main)" strokeWidth="0.5" fill="none" opacity="0.15" />
                <path d="M 0,0 A 144 144 0 0 0 144,144 A 89 89 0 0 0 233,55 A 55 55 0 0 0 178,0 A 34 34 0 0 0 144,34 A 21 21 0 0 0 165,55 A 13 13 0 0 0 178,42 A 8 8 0 0 0 170,34 A 5 5 0 0 0 165,39 A 3 3 0 0 0 168,42 A 2 2 0 0 0 170,40 A 1 1 0 0 0 169,39" stroke="var(--text-main)" strokeWidth="1.2" fill="none" />
              </svg>
            </div>
          </div>
        </section>

        {/* 3. t - trading Section */}
        <section className="story-section" id="trading">
          <div className="story-grid">
            <div className="story-content">
              <h2 className="story-title">
                <span className="title-letter">t</span>
                <span className="blinking-cursor">_</span>
                <span className="story-subtitle">— la ejecución y los resultados</span>
              </h2>
              <div className="story-quote">
                El trading institucional como nuestro motor de ingeniería y core operativo.
              </div>
              <p className="story-desc">
                El trading es el núcleo central y origen de nuestra tecnología. Aquí es donde toda la teoría matemática se traduce en ejecución real. Desplegamos nuestro propio capital mediante sistemas automatizados de alta precisión.
              </p>
              <p className="story-desc">
                Esta experiencia en mercados financieros nutre toda nuestra infraestructura tecnológica. Canalizamos este conocimiento crítico para colaborar de cerca con instituciones globales. Optimizamos decisiones complejas donde la velocidad y la precisión son fundamentales.
              </p>
            </div>

            <div className="story-visual">
              <div className="bg-letter">t</div>
              {/* SVG: S&P 500 Historical Curve */}
              <svg className="visual-svg" viewBox="0 0 400 400">
                <line x1="30" y1="350" x2="380" y2="350" stroke="var(--text-main)" strokeWidth="0.8" />
                <line x1="30" y1="40" x2="30" y2="350" stroke="var(--text-main)" strokeWidth="0.8" />
                <path d="M 30.0,347.9 L 31.7,343.2 L 33.4,346.8 L 35.0,349.1 L 36.7,349.8 L 38.4,350.0 L 40.1,349.3 L 41.8,349.3 L 43.5,347.3 L 45.1,342.2 L 46.8,342.4 L 48.5,341.8 L 50.2,339.0 L 51.9,332.5 L 53.6,335.7 L 55.2,334.2 L 56.9,328.1 L 58.6,327.0 L 60.3,327.3 L 62.0,322.2 L 63.7,317.5 L 65.3,314.0 L 67.0,311.1 L 68.7,309.9 L 70.4,312.9 L 72.1,307.1 L 73.8,304.6 L 75.4,306.3 L 77.1,298.9 L 78.8,295.1 L 80.5,293.5 L 82.2,292.8 L 83.8,292.1 L 85.5,290.1 L 87.2,288.5 L 88.9,291.6 L 90.6,294.8 L 92.3,293.0 L 93.9,296.7 L 95.6,301.2 L 97.3,299.7 L 99.0,301.7 L 100.7,306.6 L 102.4,310.3 L 104.0,308.5 L 105.7,307.6 L 107.4,315.8 L 109.1,316.6 L 110.8,320.4 L 112.5,321.9 L 114.1,326.5 L 115.8,325.0 L 117.5,329.0 L 119.2,327.3 L 120.9,325.7 L 122.5,321.3 L 124.2,316.4 L 125.9,313.3 L 127.6,310.0 L 129.3,309.3 L 131.0,301.5 L 132.6,297.8 L 134.3,293.1 L 136.0,289.0 L 137.7,283.8 L 139.4,277.3 L 141.1,275.6 L 142.7,272.2 L 144.4,277.3 L 146.1,275.5 L 147.8,268.6 L 149.5,264.8 L 151.2,260.9 L 152.8,254.9 L 154.5,259.8 L 156.2,252.4 L 157.9,253.3 L 159.6,264.8 L 161.2,327.0 L 162.9,337.6 L 164.6,325.2 L 166.3,320.1 L 168.0,313.7 L 169.7,307.0 L 171.3,302.0 L 173.0,293.8 L 174.7,288.4 L 176.4,284.2 L 178.1,276.8 L 179.8,267.5 L 181.4,256.8 L 183.1,252.8 L 184.8,250.9 L 186.5,238.4 L 188.2,231.6 L 189.9,222.5 L 191.5,221.5 L 193.2,215.5 L 194.9,205.9 L 196.6,212.2 L 198.3,210.4 L 200.0,210.1 L 201.6,200.9 L 203.3,198.3 L 205.0,196.3 L 206.7,184.9 L 208.4,185.4 L 210.0,187.1 L 211.7,180.7 L 213.4,171.0 L 215.1,163.2 L 216.8,161.6 L 218.5,165.7 L 220.1,167.1 L 221.8,156.5 L 223.5,148.4 L 225.2,147.1 L 226.9,148.9 L 228.6,149.3 L 230.2,155.7 L 231.9,166.4 L 233.6,173.7 L 235.3,180.6 L 237.0,182.3 L 238.7,189.0 L 240.3,191.5 L 242.0,189.3 L 243.7,197.3 L 245.4,209.7 L 247.1,223.5 L 248.8,224.5 L 250.4,226.2 L 252.1,234.8 L 253.8,237.1 L 255.5,239.2 L 257.2,228.8 L 258.8,219.6 L 260.5,218.4 L 262.2,210.2 L 263.9,203.8 L 265.6,198.6 L 267.3,197.7 L 268.9,197.5 L 270.6,190.0 L 272.3,190.2 L 274.0,179.3 L 275.7,175.3 L 277.4,170.5 L 279.0,160.3 L 280.7,173.2 L 282.4,180.6 L 284.1,176.5 L 285.8,183.4 L 287.5,195.8 L 289.1,184.4 L 290.8,171.1 L 292.5,167.7 L 294.2,163.0 L 295.9,143.6 L 297.5,130.6 L 299.2,122.4 L 300.9,118.3 L 302.6,110.6 L 304.3,115.4 L 306.0,115.5 L 307.6,115.1 L 309.3,104.3 L 311.0,88.2 L 312.7,92.8 L 314.4,89.5 L 316.1,90.8 L 317.7,84.4 L 319.4,87.3 L 321.1,83.2 L 322.8,82.9 L 324.5,76.8 L 326.2,82.4 L 327.8,72.4 L 329.5,73.4 L 331.2,77.4 L 332.9,81.6 L 334.6,73.2 L 336.2,69.3 L 337.9,61.6 L 339.6,65.5 L 341.3,64.7 L 343.0,67.6 L 344.7,66.7 L 346.3,67.3 L 348.0,66.8 L 349.7,60.7 L 351.4,65.4 L 353.1,65.6 L 354.8,59.5 L 356.4,71.4 L 358.1,62.6 L 359.8,53.8 L 361.5,43.7 L 363.2,46.6 L 364.9,46.8 L 366.5,51.1 L 368.2,42.7 L 369.9,45.8 L 371.6,54.0 L 373.3,62.0 L 375.0,56.8 L 376.6,49.6 L 378.3,41.6 L 380.0,40.0" stroke="var(--text-main)" strokeWidth="1.2" fill="none" />
              </svg>
            </div>
          </div>
        </section>

        {/* Footer Institucional */}
        <footer className="site-footer">
          <div className="footer-inner">
            <div className="footer-brand">
              <span className="footer-logo">qrt^</span>
              <span className="footer-meta">quantitative research &amp; trading</span>
            </div>
            <div className="footer-links">
              <Link to="/">inicio</Link>
              <a href="#quantitative" onClick={(e) => { e.preventDefault(); scrollTo('quantitative'); }}>filosofía</a>
            </div>
            <div className="footer-contact">
              <span className="footer-contact-label">contacto</span>
              <a href="mailto:contacto@qrtsolutions.com" className="footer-contact-email">
                contacto@qrtsolutions.com
              </a>
            </div>
            <p className="footer-copy">
              &copy; {new Date().getFullYear()} QRT Solutions. Todos los derechos reservados.
            </p>
          </div>
        </footer>

      </div>
    </div>
  );
};

export default Filosofia;
